import React, { useState } from 'react';
import { submitFeedback } from '../services/feedbackService';

interface FeedbackButtonsProps {
  queryId: string;
  userQuery: string;
  assistantResponse: string;
}

/**
 * Feedback buttons component for collecting user feedback on AI responses
 * Displays thumbs up/down buttons with optional comment box
 */
const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  queryId,
  userQuery,
  assistantResponse
}) => {
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<'up' | 'down' | null>(null);

  const handleFeedback = async (type: 'thumbsUp' | 'thumbsDown') => {
    if (isSubmitting || feedbackSubmitted) return;

    setIsSubmitting(true);
    setError(null);
    setSelectedFeedback(type === 'thumbsUp' ? 'up' : 'down');

    try {
      await submitFeedback({
        queryId,
        userQuery,
        assistantResponse,
        type: type === 'thumbsUp' ? 'ThumbsUp' : 'ThumbsDown',
        comment: comment || undefined
      });
      
      setFeedbackSubmitted(true);
      setShowCommentBox(false);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setFeedbackSubmitted(false);
        setSelectedFeedback(null);
        setComment('');
      }, 3000);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
      setSelectedFeedback(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCommentSubmit = async () => {
    if (!comment.trim()) {
      setError('Please enter a comment');
      return;
    }
    
    await handleFeedback('thumbsDown');
  };

  return (
    <div className="feedback-container mt-2 flex flex-col gap-2">
      <div className="feedback-buttons flex items-center gap-2">
        <button 
          onClick={() => handleFeedback('thumbsUp')}
          className={`feedback-btn px-3 py-1 rounded-md transition-all duration-200 ${
            feedbackSubmitted && selectedFeedback === 'up'
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 hover:bg-green-50 text-gray-600 hover:text-green-600'
          }`}
          disabled={isSubmitting || feedbackSubmitted}
          aria-label="This response was helpful"
          title="This response was helpful"
        >
          👍
        </button>
        
        <button 
          onClick={() => handleFeedback('thumbsDown')}
          className={`feedback-btn px-3 py-1 rounded-md transition-all duration-200 ${
            feedbackSubmitted && selectedFeedback === 'down'
              ? 'bg-red-100 text-red-700'
              : 'bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600'
          }`}
          disabled={isSubmitting || feedbackSubmitted}
          aria-label="This response needs improvement"
          title="This response needs improvement"
        >
          👎
        </button>
        
        <button
          onClick={() => setShowCommentBox(!showCommentBox)}
          className="feedback-btn px-3 py-1 rounded-md bg-gray-100 hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-all duration-200"
          aria-label="Add detailed feedback"
          title="Add detailed feedback"
          disabled={isSubmitting || feedbackSubmitted}
        >
          💬
        </button>
        
        {feedbackSubmitted && (
          <span className="feedback-confirmation text-sm text-green-600 font-medium animate-fade-in">
            ✓ Thanks for your feedback!
          </span>
        )}
        
        {isSubmitting && (
          <span className="text-sm text-gray-500">
            Submitting...
          </span>
        )}
      </div>
      
      {error && (
        <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">
          {error}
        </div>
      )}
      
      {showCommentBox && !feedbackSubmitted && (
        <div className="feedback-comment-box bg-gray-50 p-3 rounded-md border border-gray-200 animate-slide-down">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us more about your experience..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            disabled={isSubmitting}
          />
          <div className="flex gap-2 mt-2">
            <button 
              onClick={handleCommentSubmit}
              disabled={isSubmitting || !comment.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Submit Feedback
            </button>
            <button 
              onClick={() => {
                setShowCommentBox(false);
                setComment('');
                setError(null);
              }}
              disabled={isSubmitting}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackButtons;

